import { CmsSectionShell } from './CmsSectionShell.tsx';
import { SectionHeading } from '../SectionHeading.tsx';
import { CornerGuitar } from '../CornerGuitar.tsx';
import { themeOf, EYEBROW, BUTTON_FILL, type ThemeClasses } from './cmsTheme.ts';
import { formatDkkPrice } from '../../lib/format.ts';
import { PRICE_HEADER, orderFeeNote } from '../../domain/ticketFees.ts';
import { resolveOnlineSales } from '../../domain/onlineSales.ts';
import type { TicketHighlightSection, TicketHighlightItem } from '../../lib/types.ts';

/**
 * Renders 1–2 featured ticket "buy" cards from the section's own payload. Pure: all ticket data
 * (status/price/ctaUrl) is baked in at publish time, so this never fetches tickets.json.
 *
 * In v2 the card is a shade of the section background rather than a contrasting brand panel, so
 * it no longer flips fill by theme and the copy keeps the section's own ink. Each card is split
 * in two: the ticket's identity and copy on the left, the price and the CTA on the right, over
 * the guitar watermark in the combination's own fill.
 *
 * With a single card the section heading/intro move *inside* the card (rather than floating
 * above an otherwise sparse card); with two cards they stay as a shared header above the grid.
 *
 * While online sales are closed (the section's baked `onlineSales` block says so) each card shows
 * the publisher's "online sales have stopped" message in place of its price, and the button only
 * leads to the tickets page — never to a buy link.
 */
export function CmsTicketHighlightSection({ theme, guitar, heading, intro, tickets, onlineSales, orderFee }: TicketHighlightSection) {
  const t = themeOf(theme);
  const cards = tickets ?? [];
  if (cards.length === 0) return null;

  const single = cards.length === 1;
  const sales = resolveOnlineSales(onlineSales);

  return (
    <CmsSectionShell theme={theme} guitar={guitar}>
      {!single && (heading || intro) && (
        <div className="max-w-2xl">
          {heading && <SectionHeading color={t.heading} eyebrowColor={t.eyebrow} className={intro ? 'mb-3 md:mb-4' : undefined}>{heading}</SectionHeading>}
          {intro && <p className={`mb-6 md:mb-8 text-base md:text-lg ${t.body}`}>{intro}</p>}
        </div>
      )}

      <div className={`grid gap-6 md:gap-8 ${cards.length === 2 ? 'lg:grid-cols-2' : ''}`}>
        {cards.map((ticket, i) => (
          <FeaturedTicketCard
            key={`${ticket.name}-${i}`}
            ticket={ticket}
            t={t}
            heading={single ? heading : undefined}
            intro={single ? intro : undefined}
            salesClosedMessage={sales.open ? null : sales.message}
            orderFee={orderFee}
          />
        ))}
      </div>
    </CmsSectionShell>
  );
}

function FeaturedTicketCard({
  ticket, t, heading, intro, salesClosedMessage, orderFee,
}: {
  ticket: TicketHighlightItem;
  t: ThemeClasses;
  heading?: string;
  intro?: string;
  /** Set while online sales are closed: replaces the price/status with this message. */
  salesClosedMessage?: string | null;
  /** Baked-in flat per-order fee in DKK; absent = the note has no amount. */
  orderFee?: number;
}) {
  const card = t.card;
  const title = ticket.title ?? ticket.name;
  // The big line is the section heading when it has been pulled into the card (single-card
  // layout); otherwise it's the ticket's own title.
  const headline = heading ?? title;
  // The pinned ticket ALWAYS shows a button, and it always leads to the "Se billetter" page
  // (the actual buy links live there, on its tickets section). The ticket's own status only
  // changes the label and adds a small status note — it never removes the button.
  const salesClosed = !!salesClosedMessage;
  const isOnSale = !salesClosed && ticket.status === 'onSale';
  const ctaLabel = isOnSale ? (ticket.ctaLabel ?? `Køb ${title}`) : 'Se billetter';
  const statusNote = salesClosed ? null
    : ticket.status === 'comingSoon' ? 'Kommer snart'
    : ticket.status === 'soldOut' ? 'Udsolgt'
    : null;
  // Badge and CTA share the section's filled-control pair, so neither can land coral-on-coral.
  const fill = BUTTON_FILL[t.button];

  return (
    <div className={`relative overflow-hidden rounded-card p-6 md:p-8 ${card.bg} ${card.text} flex flex-col gap-6 md:flex-row md:items-start md:justify-between md:gap-10`}>
      <CornerGuitar corner="bottom-right" colorClass={t.guitar} opacityClass="opacity-[0.10]" widthClass="w-[280px]" />

      {/* Left: ticket identity + (single-card) marketing copy */}
      <div className="relative md:flex-1">
        {ticket.badge && (
          <span className={`inline-block ${EYEBROW} px-2.5 py-1 rounded-full mb-3 ${fill}`}>
            {ticket.badge}
          </span>
        )}
        {ticket.subtitle && (
          <div className={`${EYEBROW} mb-2 ${t.eyebrow}`}>{ticket.subtitle}</div>
        )}
        <h3 className="font-display text-[22px] md:text-2xl leading-tight">{headline}</h3>
        {/* The section intro folds into the single card; a two-card layout uses each card's
            own description instead. */}
        {intro
          ? <p className={`mt-3 max-w-md text-base md:text-lg ${card.muted}`}>{intro}</p>
          : ticket.description && <p className={`mt-3 max-w-md text-base md:text-lg ${card.muted}`}>{ticket.description}</p>}
      </div>

      {/* Right: price, availability and CTA */}
      <div className="relative md:shrink-0 md:text-right md:min-w-[220px]">
        {salesClosed ? (
          <p className={`max-w-xs md:ml-auto text-base md:text-lg ${card.muted}`} role="status">
            {salesClosedMessage}
          </p>
        ) : ticket.price !== undefined && (
          <>
            <div className={`${EYEBROW} ${card.muted}`}>{PRICE_HEADER}</div>
            <div className={`font-display text-3xl md:text-4xl mt-1 tabular-nums ${card.text}`}>{formatDkkPrice(ticket.price)}</div>
            <p className={`mt-2 max-w-xs md:ml-auto text-sm ${card.muted}`}>{orderFeeNote(orderFee)}</p>
          </>
        )}
        {statusNote && (
          <div className={`mt-3 ${EYEBROW} ${card.muted}`}>{statusNote}</div>
        )}
        <a
          href="/billetter"
          className={`mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full py-3 px-5 font-medium transition-colors ${fill}`}
        >
          {ctaLabel}
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
        </a>
      </div>
    </div>
  );
}
